import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Seamless cross-fade page transition.
 * Renders the previous route's children for a brief overlap window
 * while the new route fades in, producing a clean dissolve with no flash.
 */
const PageTransition = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [displayKey, setDisplayKey] = useState(location.pathname);
  const [stage, setStage] = useState<"idle" | "out" | "in">("idle");
  const prevPath = useRef(location.pathname);

  // When the route changes, fade out, swap, then fade in.
  useEffect(() => {
    if (location.pathname === prevPath.current) {
      setDisplayChildren(children);
      return;
    }
    prevPath.current = location.pathname;
    setStage("out");
    const t1 = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "auto" });
      setDisplayChildren(children);
      setDisplayKey(location.pathname);
      setStage("in");
      requestAnimationFrame(() =>
        requestAnimationFrame(() => setStage("idle"))
      );
    }, 380);
    return () => clearTimeout(t1);
  }, [location.pathname, children]);

  // Keep latest children synced when no transition active
  useEffect(() => {
    if (stage === "idle") setDisplayChildren(children);
  }, [children, stage]);

  const opacity = stage === "out" ? 0 : 1;

  return (
    <>
      <div
        key={displayKey}
        style={{
          opacity,
          transition: "opacity 380ms ease",
          willChange: "opacity",
        }}
      >
        {displayChildren}
      </div>
      {/* Soft top overlay veil for a polished dissolve */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[200]"
        style={{
          background: "white",
          opacity: stage === "out" ? 0.55 : 0,
          transition: "opacity 380ms ease",
        }}
      />
    </>
  );
};

export default PageTransition;
